import React, { useContext, useState } from "react";
import { IMAGES, SVG_IMAGES } from "../../assets";
import { STRING, STRINGS } from "../../constant";
import { LAButton, LAText, LATextInput, LAView } from "../../components";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthProvider";
import { BundleContext } from "../../context/BundleProvider";
import { GET_APP_DOMAIN_LIST } from "../../../app_constant";
import { QUESTION_PRIORITY, SHOW_TOAST } from "../../constant/utils";
import BackArrowIcon from "../../components/BackArrowIcon";
import { ThemeContext } from "../../context/ThemeProvider";
import { MixpanelContext } from "../../context/MixpanelProvider";
import PracticeTestDialog from "../../components/PracticeTestDialog";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLock, 
  faChevronDown, 
  faChevronUp, 
  faCheck,
  faCircle
} from '@fortawesome/free-solid-svg-icons';
import { color } from "chart.js/helpers";
import { getCustomTestIconBg, getThemeBlackWhiteColor } from "../../utils/themeUtils";

const CustomTestBuilder = () => {
  const navigate = useNavigate();

  const { profile, community } = useContext(AuthContext);
  const { selectedCertificate } = useContext(BundleContext);
  const { trackCustomTest } = useContext(MixpanelContext);
  const { theme, currentTheme, getThemeName } = useContext(ThemeContext);

  const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(selectedCertificate);
  const [selectedDomains, setSelectedDomains] = useState(APP_DOMAIN_LIST);
  const [numberOfQuestion, setNumberOfQuestion] = useState(5);
  const [timeAmount, setTimeAmount] = useState(0);
  const [priority, setPriority] = useState(STRING.default);
  const [isShowAnswer, setShowAnswer] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [testDetails, setTestDetails] = useState(null);

  // Card animations
  const cardVariants = {
    hover: { 
      scale: 1.02,
      boxShadow: "0px 8px 15px rgba(0,0,0,0.1)",
      transition: { type: "spring", stiffness: 400, damping: 10 }
    }
  };

  // Helper function to get icon background color
  const getIconBg = () => {
    return currentTheme === 'light' ? theme?.icon_bg?.custom_light : theme?.icon_bg?.custom_dark;
  }

  const getCardBackground = (iconBg) => {
    const baseColor = currentTheme === 'light' ? theme?.homeCard?.start_light : theme?.homeCard?.start_dark;
    const darkerShade = currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark;   

    return {
      background: `linear-gradient(to right, ${baseColor}, ${darkerShade})`,
      borderLeft: `4px solid ${iconBg || (currentTheme === 'light' ? theme?.icon_bg?.custom_light : theme?.icon_bg?.custom_dark)}`
    };
  };

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };
  
  // Helper function for border color
  const getBorderColor = () => {
    return currentTheme === 'light' 
      ? 'rgba(0,0,0,0.1)' 
      : 'rgba(255,255,255,0.2)';
  };

  const getOptionColor = (selected) => {
    if (selected) {
      return currentTheme === 'light' 
        ? theme?.option?.selected?.light 
        : theme?.option?.selected?.dark;
    }
    return currentTheme === 'light' 
      ? theme?.option?.unselected?.light 
      : theme?.option?.unselected?.dark;
  };

  const getHoverColor = () => {
    return currentTheme === 'light' 
      ? "hover:bg-[" + theme?.hover?.light + "]"
      : "hover:bg-[" + theme?.hover?.dark + "]";
  };

  function onStart() {
    if (selectedDomains.length > 0) {
      const domain_ids = selectedDomains.map((e) => e.id);
      const domain_names = selectedDomains.map((e) => e.domain_name);

      trackCustomTest(
        domain_ids,
        domain_names,
        numberOfQuestion,
        timeAmount,
        isShowAnswer,
        priority
      );
      const questionTime = profile?.time_per_question
        ? profile?.time_per_question
        : community?.total_time_per_question ?? 90;

      let test_time = questionTime * numberOfQuestion;

      const item = {
        test_name: "Custom Test",
        num_questions: numberOfQuestion,
        test_time: test_time,
        total_time: timeAmount,
        topic_ids: domain_ids,
        is_custom_test: true,
        is_show_answer: isShowAnswer,
        priority: priority,
      };
      setTestDetails(item);
      setIsDialogOpen(true);
    } else {
      SHOW_TOAST("Please select domains");
    }
  }
  
  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleTestStart = () => {
    trackCustomTest(
      testDetails.topic_ids,
      testDetails.domains,
      testDetails.num_questions,
      testDetails.total_time,
      testDetails.is_show_answer,
      testDetails.priority
    );

    setIsDialogOpen(false);
    navigate("/test", { state: testDetails });
  };

  const handleChoosePlan = () => {
    setIsDialogOpen(false);
    navigate("/chooseplan");
  };

  function onSelectItem(item, isSelected) {
    if (isSelected) {
      const array = selectedDomains?.filter((e) => e.id != item.id);
      setSelectedDomains(array);
    } else {
      const array = [...selectedDomains];
      array.push(item);
      setSelectedDomains(array);
    }
  }

  function onAllSelection(isSelected) {
    if (isSelected) {
      setSelectedDomains([]);
    } else {
      setSelectedDomains(APP_DOMAIN_LIST);
    }
  }

  function getScoreOfTopic(id) {
    const scores = profile?.readiness_scores?.topic_progress ?? [];
    const topics = scores.filter((e) => e.domain_id == id);
    if (topics.length > 0) {
      const value = topics[0]?.readiness_score ?? "0";
      const intValue = parseInt(value);
      if (intValue >= 70) {
        return theme.COLOR_GREEN;
      } else if (intValue < 40) {
        return theme.COLOR_RED;
      } else {
        return theme.COLOR_YELLOW;
      }
    }
    return theme.COLOR_RED;
  }

  const domains_ids = APP_DOMAIN_LIST?.map((e) => e.id);
  const selected_ids = selectedDomains?.map((e) => e.id);
  const isSelected = domains_ids.length == selected_ids.length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          className="rounded-lg border-2 p-2 mr-4 flex items-center justify-center"
          style={{ borderColor: currentTheme === "light" ? theme?.border_color?.light : theme?.border_color?.dark }}
          onClick={() => navigate("/")}
        >
          <BackArrowIcon
            color={currentTheme === "light" ? theme?.text?.light : theme?.text?.dark}
          />
        </button>
        <h1 
          className="text-2xl font-semibold"
          style={{ color: getTextColor() }}
        >
          {STRINGS.custom_test_builder}
        </h1>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Domain Selection */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm"
          whileHover="hover"
          variants={cardVariants}
        >
          <div 
            className="h-full"
            style={getCardBackground(getIconBg())}
          >
            <div className="p-6">
              {/* Domain Dropdown Header */}
              <div 
                
                className="flex items-center justify-between p-3 rounded-lg mb-4"
                
              >
                <h2 
                className="text-lg font-semibold mb-4"
                style={{ color: getTextColor() }}
              >
                Select Domains ({selectedDomains?.length})
              </h2>
                
                
              </div>

              {/* Domain List */}
              
                <div className="space-y-3  overflow-y-auto pr-2">
                  {/* All Domains Option */}
                  <div 
                    onClick={() => onAllSelection(isSelected)}
                    className="flex items-center gap-3 cursor-pointer p-4 rounded-lg transition-colors hover:bg-gray-500 dark:hover:bg-gray-700"
                  >
                    <div 
                      className="w-5 h-5 flex items-center justify-center rounded-md border-2"
                      style={{
                        backgroundColor: getCustomTestIconBg(theme, currentTheme),
                        borderColor: getCustomTestIconBg(theme, currentTheme),
                        color: getTextColor()
                      }}
                    >
                      {isSelected && (
                        <FontAwesomeIcon 
                          icon={faCheck} 
                          className="text-white text-xs" 
                        />
                      )}
                    </div>
                    <span 
                      className="font-semibold"
                      style={{ color: getTextColor() }}
                    >
                      All Domains
                    </span>
                  </div>

                  {/* Individual Domains */}
                  {APP_DOMAIN_LIST?.map((item, idx) => {
                    const isItemSelected = selectedDomains.filter((e) => e.id == item.id).length > 0;
                    const scoreColor = getScoreOfTopic(item.id);
                    
                    return (
                      <div 
                        key={item?.id + idx}
                        onClick={() => onSelectItem(item, isItemSelected)}
                        className="flex items-center gap-3 cursor-pointer p-4 rounded-lg transition-colors hover:bg-gray-500 dark:hover:bg-gray-700"
                      >
                        <div 
                          className="w-5 h-5 flex items-center justify-center rounded-md border-2"
                          style={{
                            backgroundColor:  getCustomTestIconBg(theme, currentTheme),
                            borderColor:  getCustomTestIconBg(theme, currentTheme),
                            color: getTextColor()
                          }}
                        >
                          {isItemSelected && (
                            <FontAwesomeIcon 
                              icon={faCheck} 
                              className={`text-white text-xs`} 
                            /> 
                          )} 
                        </div>
                        <span 
                          className="font-semibold flex-1"
                          style={{ color: getTextColor() }}
                        >
                          {item?.domain_name}
                        </span>
                        <div
                          style={{ backgroundColor: scoreColor }}
                          className="h-3 w-3 rounded-full"
                        ></div>
                      </div>
                    );
                  })}
                </div>
              
            </div>
          </div>
        </motion.div>

        {/* Test Configuration */}
        <div className="space-y-6">
          {/* Number of Questions */}
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm"
            whileHover="hover"
            variants={cardVariants}
          >
            <div 
            className="h-full"
            style={getCardBackground(getIconBg())}
          >
            <div className="p-6">
              <h2 
                className="text-lg font-semibold mb-4"
                style={{ color: getTextColor() }}
              >
                Number of Questions
              </h2>
              <div className="grid grid-cols-5 gap-3">
                {[5, 10, 25, 50, 100].map((num, idx) => {
                  
                  const isActive = num === numberOfQuestion;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        setTimeAmount(0);
                        setNumberOfQuestion(num);
                      }}
                      className="relative rounded-xl flex items-center justify-center h-14 w-full hover:bg-gray-500 dark:hover:bg-gray-700"
                      style={{
                        backgroundColor: isActive ? getCustomTestIconBg(theme, currentTheme) : getOptionColor(isActive),
                        color: getTextColor()
                      }}
                    >
                      
                      <span className="font-medium">{num}</span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center my-6">
                <div className="flex-1 border-b border-dashed" style={{ borderColor: getBorderColor() }}></div>
                <span className="px-4" style={{ color: getTextColor() }}>Or</span>
                <div className="flex-1 border-b border-dashed" style={{ borderColor: getBorderColor() }}></div>
              </div>

              <h2 
                className="text-lg font-semibold mb-4"
                style={{ color: getTextColor() }}
              >
                Time (in minutes)
              </h2>
              <div className="grid grid-cols-5 gap-3">
                {[5, 10, 20, 30, 60].map((time, idx) => {
                  const isActive = time === timeAmount;
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        setNumberOfQuestion(0);
                        setTimeAmount(time);
                      }}
                      className="relative rounded-xl flex items-center justify-center h-14 w-full"
                      style={{
                        backgroundColor: isActive ? getCustomTestIconBg(theme, currentTheme) : getOptionColor(isActive),
                        color: isActive
                          ? '#ffffff'
                          : getTextColor()
                      }}
                    >
                      
                      <span className="font-medium">{time}</span>
                    </button>
                  );
                })}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Show Answer Option */}
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm"
            whileHover="hover"
            variants={cardVariants}
          >
            <div 
            className="h-full"
            style={getCardBackground(getIconBg())}
          >
            <div className="p-6">
              <div className="flex items-center justify-between">
                <h3 
                  className="font-semibold"
                  style={{ color: getTextColor() }}
                >
                  Show answer as I go
                </h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isShowAnswer}
                    onChange={(e) => setShowAnswer(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-600 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
                </label>
              </div>
            </div>
            </div>
          </motion.div>

          {/* Question Priority */}
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm"
            whileHover="hover"
            variants={cardVariants}
          >
            <div 
            className="h-full"
            style={getCardBackground(getIconBg())}
          >
            <div className="p-6">
              <h2 
                className="text-lg font-semibold mb-4"
                style={{ color: getTextColor() }}
              >
                Which Questions to Prioritize?
              </h2>
              <div className="space-y-3">
                {QUESTION_PRIORITY?.map((ele, idx) => {
                  const isChecked = ele.key === priority;
                  
                  return (
                    <div 
                      key={idx}
                      onClick={() => setPriority(ele.key)}
                      className="flex items-center gap-3 cursor-pointer p-2 hover:bg-gray-500 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                     <div 
                          className="w-5 h-5 flex items-center justify-center rounded-full border-2"
                          style={{
                            backgroundColor: getCustomTestIconBg(theme, currentTheme),
                            borderColor: getCustomTestIconBg(theme, currentTheme),
                            color: getTextColor()
                          }}
                        >
                      
                        {isChecked && (
                          <FontAwesomeIcon 
                            icon={faCheck} 
                            className="text-white text-xs" 
                          />
                        )}
                      </div>
                      <span 
                        className="font-medium"
                        style={{ color: getTextColor() }}
                      >
                        {ele?.title}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
          </motion.div>

          {/* Start Button */}
          <div className="flex justify-center items-center mt-6">
            <button
              className="w-2/3 py-4 px-6 text-white font-medium rounded-xl shadow-lg transition-transform transform hover:scale-105"
              style={{ backgroundColor: getIconBg() }}
              onClick={onStart}
            >
              {STRING.get_started}
            </button>
          </div>
        </div>
      </div>

      {/* Dialog */}
      {testDetails && (
        <PracticeTestDialog
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          onStart={handleTestStart}
          onChoosePlan={handleChoosePlan}
          testDetails={testDetails}
          iconBg={getIconBg()}
        />
      )}
    </div>
  );
};

export default CustomTestBuilder;